import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, Plus, Search, User } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AddClientDialog } from "@/components/features/clients/AddClientDialog";
import { clientKeys } from "@/lib/queryKeys";
import { listClients } from "@/services/clientService";
import type { Client } from "@/services/clientService";

/**
 * "Consumidor final" is a synthetic option — emitting with no real client
 * becomes an anonymous e-Ticket. The invoice form models that as a null
 * client, so this id never leaves the dialog.
 */
const CONSUMIDOR_FINAL_ID = "__consumidor_final__";

/** Same page size the invoice form itself queries, so the cache is shared. */
const CLIENT_PAGE_SIZE = 50;

const MAX_VISIBLE_CLIENTS = 8;

interface NuevaFacturaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Portal port of the mobile Nueva-factura bottom sheet
 * (apps/mobile/components/NuevaFacturaSheet.tsx): ask who the invoice is for
 * before opening the 2085-line form, then land there with the receiver
 * already applied.
 *
 * Two-step commit (pick a row, then Continuar) mirrors mobile deliberately —
 * navigating on row click would turn a misclick into an unwanted trip into
 * the form.
 *
 * Mobile's QR-scan row is not ported: it pushes to a camera route with no
 * desktop equivalent.
 */
export function NuevaFacturaDialog({
  open,
  onOpenChange,
}: NuevaFacturaDialogProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addClientOpen, setAddClientOpen] = useState(false);

  // Reset when the dialog closes so re-opening starts clean, matching the
  // mobile sheet's onDismiss.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedId(null);
    }
  }, [open]);

  // With an empty search this key is byte-identical to the invoice form's
  // own clients query, so opening the dialog warms the form's cache instead
  // of issuing a second request.
  const trimmedSearch = search.trim();
  const { data, isLoading } = useQuery({
    queryKey: clientKeys.list({
      page: 1,
      limit: CLIENT_PAGE_SIZE,
      search: trimmedSearch || undefined,
    }),
    queryFn: () =>
      listClients({
        page: 1,
        limit: CLIENT_PAGE_SIZE,
        search: trimmedSearch || undefined,
      }),
    enabled: open,
  });

  const clients: Client[] = data?.data ?? [];
  const visible = clients.slice(0, MAX_VISIBLE_CLIENTS);

  const handleContinue = (): void => {
    if (!selectedId) return;
    const picked = selectedId === CONSUMIDOR_FINAL_ID ? null : selectedId;
    onOpenChange(false);
    // Consumidor final navigates with no param, exactly as mobile does — the
    // form's default state already IS the unselected receiver.
    navigate(picked ? `/invoices/new?clientId=${picked}` : "/invoices/new");
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("invoices.nuevaFacturaDialog.title", "¿A quién le facturás?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "invoices.nuevaFacturaDialog.description",
              "Empezá a tipear el nombre o RUT y elegí de tu lista de clientes.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--fx-ink-400)]"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              "invoices.nuevaFacturaDialog.searchPlaceholder",
              "Buscar cliente…",
            )}
            className="pl-9"
            autoFocus
          />
        </div>

        {/* Fixed height, not max-height: the list used to start empty and grow
            as clients arrived, which jumped the whole centred dialog. Holding
            the box at one size means only its contents change. */}
        <div className="h-[300px] overflow-y-auto rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] dark:border-[var(--dk-border)]">
          <PickerRow
            selected={selectedId === CONSUMIDOR_FINAL_ID}
            onSelect={() => setSelectedId(CONSUMIDOR_FINAL_ID)}
            icon={<User className="h-4 w-4" aria-hidden />}
            title={t(
              "invoices.nuevaFacturaDialog.consumidorFinal",
              "Consumidor final",
            )}
            sub={t(
              "invoices.nuevaFacturaDialog.consumidorFinalSub",
              "Sin datos del receptor",
            )}
          />

          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <PickerRowSkeleton key={`skeleton-${i}`} />
              ))
            : visible.map((c) => (
                <PickerRow
                  key={c.id}
                  selected={selectedId === c.id}
                  onSelect={() => setSelectedId(c.id)}
                  icon={
                    <span className="text-[11px] font-bold">
                      {initialsFor(c.displayName || c.legalName)}
                    </span>
                  }
                  title={c.displayName || c.legalName}
                  sub={c.rut ?? c.docRecepExt ?? undefined}
                />
              ))}

          {!isLoading && clients.length === 0 && trimmedSearch.length > 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[var(--fx-ink-500)]">
              {t(
                "invoices.nuevaFacturaDialog.noResults",
                'Sin resultados para "{{query}}".',
                { query: trimmedSearch },
              )}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => {
              // Close the picker before opening the form — two stacked Radix
              // dialogs would trap focus in the wrong one.
              onOpenChange(false);
              setAddClientOpen(true);
            }}
            className="flex w-full items-center gap-3 border-t border-[var(--fx-ink-100)] px-4 py-3 text-left transition-colors hover:bg-[var(--fx-s-overlay-hover)] dark:border-[var(--dk-border)] dark:hover:bg-[var(--fx-s-overlay-hover)]"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[var(--fx-accent-100)] text-[var(--fx-accent-700)]">
              <Plus className="h-4 w-4" aria-hidden />
            </span>
            <span className="text-sm font-semibold">
              {t("invoices.nuevaFacturaDialog.addClient", "Agregar cliente")}
            </span>
          </button>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="fx-btn fx-btn-ghost"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel", "Cancelar")}
          </button>
          <button
            type="button"
            className="fx-btn fx-btn-accent"
            disabled={!selectedId}
            aria-disabled={!selectedId}
            onClick={handleContinue}
          >
            {t("invoices.nuevaFacturaDialog.continue", "Continuar →")}
          </button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Same dialog the invoice form itself uses to add a client, rather than
        a redirect to /clients/new — the user opened the picker to invoice
        someone, so bouncing them to a full page loses that intent. On success
        we go straight to the form with the new client applied: selecting them
        in the picker instead would show an empty row until the client list
        refetched. */}
    <AddClientDialog
      open={addClientOpen}
      onOpenChange={setAddClientOpen}
      onCreated={(client) => {
        setAddClientOpen(false);
        navigate(`/invoices/new?clientId=${client.id}`);
      }}
    />
    </>
  );
}

interface PickerRowProps {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  sub?: string;
}

/**
 * Row hover is an accent tint rather than paper-2: paper-2 (#ECE6D9) and the
 * avatar tile's ink-100 (#EBE6DD) are a point apart, so the avatar dissolved
 * into the row on hover. Dark mode had the same collision, with both the row
 * hover and the tile sitting on --dk-surface2. The tile also moves to ink-200
 * so it holds an edge against white and against the tint.
 */
function PickerRow({
  selected,
  onSelect,
  icon,
  title,
  sub,
}: PickerRowProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className="flex w-full items-center gap-3 border-b border-[var(--fx-ink-100)] px-4 py-3 text-left transition-colors hover:bg-[var(--fx-s-overlay-hover)] dark:border-[var(--dk-border)] dark:hover:bg-[var(--fx-s-overlay-hover)]"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[var(--fx-ink-200)] text-[var(--fx-ink-700)] dark:bg-[var(--dk-surface2)] dark:text-[var(--dk-text-soft)]">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{title}</span>
        {sub ? (
          <span className="block truncate font-mono text-[11px] text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]">
            {sub}
          </span>
        ) : null}
      </span>
      {selected ? (
        <Check
          className="h-4 w-4 shrink-0 text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]"
          aria-hidden
        />
      ) : null}
    </button>
  );
}

/**
 * Placeholder row at the exact height of a real one, so the list is the same
 * size before and after the clients arrive.
 */
function PickerRowSkeleton(): JSX.Element {
  return (
    <div
      className="flex w-full items-center gap-3 border-b border-[var(--fx-ink-100)] px-4 py-3 dark:border-[var(--dk-border)]"
      aria-hidden
    >
      <Skeleton className="h-8 w-8 shrink-0 rounded-[10px]" />
      <div className="min-w-0 flex-1">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="mt-1.5 h-3 w-1/4" />
      </div>
    </div>
  );
}

function initialsFor(name: string): string {
  // Destructured rather than indexed so the checker narrows these itself —
  // length checks don't tell it parts[0] is defined, which is what the
  // non-null assertions here were papering over.
  const [first, second] = name.trim().split(/\s+/).filter(Boolean);
  if (!first) return "?";
  if (!second) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}
